/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { type FC, useEffect, useMemo, useState } from "react"
import { X, Expand } from "lucide-react"
import { Outlet, useLocation, useMatches, useNavigate } from "react-router"
import { NucleusChatView } from "../../containers/NucleusChatView"
import { RunsProvider } from "../../contexts/RunsContext"
import { RunSelectionProvider } from "../../hooks/runSelectionContext"
import { theme } from "../../styles/theme/theme"
import { Sidebar, SIDEBAR_WIDTH } from "./Sidebar"
import { RunHeader } from "./RunHeader"
import { SlimHeader } from "./SlimHeader"
import { ViewAsBanner } from "../../containers/ViewAs/ViewAsBanner"
import { FeedbackModal } from "./FeedbackModal"

/** Per-route chrome opt-in. A route declares `handle: { chrome: "slim" }` to
 *  drop the full RunHeader entirely and overlay the icon-only SlimHeader on the
 *  page content's top-right corner (the embedded page shifts its own toolbar
 *  left to share the row — see SlimHeader); the default keeps the full header. */
type RouteChrome = "full" | "slim"
const isRouteHandle = (h: unknown): h is { chrome?: RouteChrome } =>
  typeof h === "object" && h !== null

const layoutStyles = {
  root: css`
    height: 100vh;
    background-color: ${theme.colors.background};
    overflow: hidden;
  `,
  inner: css`
    display: flex;
    height: 100%;
  `,
  mainContent: css`
    flex: 1;
    margin-left: ${SIDEBAR_WIDTH}px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
    position: relative;
  `,
  contentRow: (dockOpen: boolean, isNucleusPage: boolean) => css`
    display: grid;
    grid-template-columns: minmax(0, 1fr) ${dockOpen && !isNucleusPage ? "26rem" : "0"};
    flex: 1;
    min-height: 0;
    transition: grid-template-columns 0.22s ease;

    @media (max-width: 1180px) {
      grid-template-columns: minmax(0, 1fr);
    }
  `,
  pageContent: css`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    /* Anchor for the SlimHeader overlay on chromeless routes. */
    position: relative;
  `,
  dockShell: (dockOpen: boolean, isNucleusPage: boolean) => css`
    display: ${dockOpen && !isNucleusPage ? "flex" : "none"};
    min-height: 0;
    flex-direction: column;
    border-left: 1px solid ${theme.colors.border};
    background:
      radial-gradient(circle at top right, rgba(59, 130, 246, 0.08), transparent 20rem),
      ${theme.colors.background};
    box-shadow: -12px 0 24px rgba(15, 23, 42, 0.06);

    @media (max-width: 1180px) {
      position: absolute;
      inset: 0;
      z-index: 30;
      display: ${dockOpen && !isNucleusPage ? "flex" : "none"};
      border-left: none;
      box-shadow: 0 0 0 1px ${theme.colors.border}, 0 24px 48px rgba(15, 23, 42, 0.18);
    }
  `,
  dockHeader: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid ${theme.colors.border};
    background: rgba(255, 255, 255, 0.88);
    backdrop-filter: blur(8px);
  `,
  dockTitleWrap: css`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.125rem;
  `,
  dockEyebrow: css`
    margin: 0;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${theme.colors.nucleus};
  `,
  dockTitle: css`
    margin: 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: ${theme.colors.foreground};
  `,
  dockSubtitle: css`
    margin: 0;
    font-size: 0.75rem;
    color: ${theme.colors.mutedForeground};
  `,
  dockActions: css`
    display: flex;
    align-items: center;
    gap: 0.35rem;
  `,
  dockActionButton: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: 1px solid ${theme.colors.border};
    border-radius: 0.75rem;
    background: ${theme.colors.background};
    color: ${theme.colors.mutedForeground};
    cursor: pointer;
    transition:
      background-color 0.16s ease,
      color 0.16s ease,
      transform 0.16s ease;

    &:hover {
      background: ${theme.colors.gray50};
      color: ${theme.colors.foreground};
      transform: translateY(-1px);
    }
  `,
  dockBody: css`
    flex: 1;
    min-height: 0;
    overflow: hidden;
  `,
}

const getCurrentViewLabel = (pathname: string) => {
  if (pathname.startsWith("/data-hub")) return "Input Data";
  if (pathname === "/data" || pathname.startsWith("/data/")) return "Data";
  if (pathname.startsWith("/demand/validation")) return "Demand validation";
  if (pathname.startsWith("/replenishment")) return "Replenishment";
  if (pathname.startsWith("/shipments/planning-console")) return "Planning Console";
  if (pathname.startsWith("/shipments/reporting")) return "Shipments Reporting";
  if (pathname.startsWith("/shipments/validation")) return "Shipment validation";
  if (pathname.startsWith("/shipments/configuration")) return "Shipment configuration";
  if (pathname.startsWith("/runs/history")) return "Run History";
  if (pathname.startsWith("/docs")) return "Docs";
  if (pathname.startsWith("/workflows")) return "Workflows";
  if (pathname.startsWith("/nucleus")) return "Nucleus workspace";
  return "Current view";
}

const DashboardLayout: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isNucleusDockOpen, setIsNucleusDockOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const isNucleusPage = location.pathname === "/nucleus";
  // Chrome mode comes from the matched route's `handle` (see routes.tsx), not a
  // hardcoded pathname list — any full-bleed page can opt into the slim header.
  const matches = useMatches();
  const chrome: RouteChrome = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i--) {
      const handle = matches[i].handle;
      if (isRouteHandle(handle) && handle.chrome) return handle.chrome;
    }
    return "full";
  }, [matches]);
  const currentViewLabel = useMemo(() => getCurrentViewLabel(location.pathname), [location.pathname]);

  useEffect(() => {
    if (isNucleusPage) {
      setIsNucleusDockOpen(false);
    }
  }, [isNucleusPage]);

  return (
    <RunsProvider>
      <RunSelectionProvider>
        <div css={layoutStyles.root}>
          <div css={layoutStyles.inner}>
            <Sidebar
              isNucleusDockOpen={isNucleusDockOpen}
              onNucleusToggle={() => setIsNucleusDockOpen((current) => !current)}
            />
            <FeedbackModal
              open={isFeedbackOpen}
              onClose={() => setIsFeedbackOpen(false)}
            />
            <div css={layoutStyles.mainContent}>
              <ViewAsBanner />
              {chrome === "full" && (
                <RunHeader onSendFeedback={() => setIsFeedbackOpen(true)} />
              )}
              <div css={layoutStyles.contentRow(isNucleusDockOpen, isNucleusPage)}>
                <div css={layoutStyles.pageContent}>
                  {chrome === "slim" && (
                    <SlimHeader onSendFeedback={() => setIsFeedbackOpen(true)} />
                  )}
                  <Outlet />
                </div>

                <aside css={layoutStyles.dockShell(isNucleusDockOpen, isNucleusPage)}>
                  <div css={layoutStyles.dockHeader}>
                    <div css={layoutStyles.dockTitleWrap}>
                      <p css={layoutStyles.dockEyebrow}>Nucleus</p>
                      <h2 css={layoutStyles.dockTitle}>Following {currentViewLabel}</h2>
                      <p css={layoutStyles.dockSubtitle}>Ask about the run and the screen you are currently looking at.</p>
                    </div>

                    <div css={layoutStyles.dockActions}>
                      <button
                        type="button"
                        css={layoutStyles.dockActionButton}
                        onClick={() => navigate("/nucleus")}
                        aria-label="Expand Nucleus"
                        title="Open full page"
                      >
                        <Expand size={16} />
                      </button>
                      <button
                        type="button"
                        css={layoutStyles.dockActionButton}
                        onClick={() => setIsNucleusDockOpen(false)}
                        aria-label="Close Nucleus"
                        title="Close sidebar"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>

                  <div css={layoutStyles.dockBody}>
                    <NucleusChatView
                      shellMode="dock"
                      currentViewLabel={currentViewLabel}
                      onExpand={() => navigate("/nucleus")}
                      onClose={() => setIsNucleusDockOpen(false)}
                    />
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </div>
      </RunSelectionProvider>
    </RunsProvider>
  )
}

export default DashboardLayout
